import { Position } from "types/battle-engine/battle";
import { GRID_WIDTH, GRID_HEIGHT } from "types/battle-engine/constants";

/**
 * Collision data utility for battle grid positioning.
 * Provides walkable positions sorted by top-left priority (Y ascending, then X ascending).
 */

export interface GridCollisionData {
  walkablePositions: Position[];
}

/**
 * Generates a default full grid with all positions walkable.
 * Sorted by Y ascending (top first), then X ascending (left first).
 */
function generateDefaultGrid(): Position[] {
  const positions: Position[] = [];
  for (let y = 0; y < GRID_HEIGHT; y++) {
    for (let x = 0; x < GRID_WIDTH; x++) {
      positions.push({ x, y });
    }
  }
  return positions;
}

/**
 * Sorts positions by top-left priority: Y ascending, then X ascending.
 */
function sortPositionsTopLeft(positions: Position[]): Position[] {
  return [...positions].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
}

/**
 * Sorts positions by bottom-left priority: Y descending, then X ascending.
 */
function sortPositionsBottomLeft(positions: Position[]): Position[] {
  return [...positions].sort((a, b) => {
    if (a.y !== b.y) return b.y - a.y; // Y descending (bottom first)
    return a.x - b.x; // X ascending (left first)
  });
}

// Default grid with all positions walkable, sorted top-left first
const DEFAULT_GRID = generateDefaultGrid();

/**
 * Cache for loaded collision data by mapId.
 */
const collisionCache = new Map<string, GridCollisionData>();

/**
 * Get collision data for a map.
 * Returns walkable positions sorted by top-left priority.
 *
 * @param mapId - The map ID (e.g., "fire/fire (1)")
 * @returns Grid collision data with walkable positions
 */
export function getCollisionData(mapId: string): GridCollisionData {
  // Check cache first
  if (collisionCache.has(mapId)) {
    return collisionCache.get(mapId)!;
  }

  // For now, return default grid for all maps
  // TODO: Load actual collision data when mapId naming is aligned with collision files
  const data: GridCollisionData = {
    walkablePositions: DEFAULT_GRID,
  };

  collisionCache.set(mapId, data);
  return data;
}

/**
 * Get starting positions for enemies (Team B) based on collision data.
 * Returns positions sorted by top-left priority, taking only what's needed.
 *
 * @param mapId - The map ID
 * @param count - Number of positions needed
 * @returns Array of positions for enemy spawning
 */
export function getEnemySpawnPositions(
  mapId: string,
  count: number,
): Position[] {
  const collisionData = getCollisionData(mapId);
  const sortedPositions = sortPositionsTopLeft(collisionData.walkablePositions);

  // Take the first N positions (top-left-most)
  return sortedPositions.slice(0, Math.min(count, sortedPositions.length));
}

/**
 * Get starting positions for player team (Team A) based on collision data.
 * Returns positions sorted by bottom-left priority, taking only what's needed.
 * Filters to middle-bottom rows (rows 6-9) to ensure visibility above UI overlay.
 *
 * @param mapId - The map ID
 * @param count - Number of positions needed
 * @returns Array of positions for player spawning
 */
export function getPlayerSpawnPositions(
  mapId: string,
  count: number,
): Position[] {
  const collisionData = getCollisionData(mapId);

  // Filter to middle-bottom rows (6-9) to ensure visibility above overlay
  const middleBottomPositions = collisionData.walkablePositions.filter(
    (p) => p.y >= 6 && p.y <= 9,
  );

  const sortedPositions = sortPositionsBottomLeft(middleBottomPositions);

  // Take the first N positions (bottom-left-most within the filtered range)
  return sortedPositions.slice(0, Math.min(count, sortedPositions.length));
}
